import { Button, Text } from '@mantine/core';
import { FlexExtended } from '@dealstack/frontend/shared/components/Flex/FlexExtended';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useInvestorNavigation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/NavigationFooter/hook/useInvestorNavigation';
import { DataKYCFileTctWithFile, GetKYCInvestorInstanceRes } from '@dealstack/shared/lib/generated';
import { useKeyboardNavigation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/NavigationFooter/hook/useKeyboardNavigation';
import { useScrollFileCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/NavigationFooter/hook/useScrollFileCard';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';

interface Props {
	filesForStep: DataKYCFileTctWithFile[];
	filteredInvestors: GetKYCInvestorInstanceRes[] | undefined;
	moveToPreviousStep: () => void;
	moveToNextStep: () => void;
	moveToStep: (stepIndex: number) => void;
	canMoveToNextStep: boolean;
	canMoveToPreviousStep: boolean;
	currentInvestor: GetKYCInvestorInstanceRes | undefined;
	selectedKycFile: DataKYCFileTctWithFile | undefined;
}

export const NavigationFooter = ({
	filesForStep,
	currentInvestor,
	filteredInvestors,
	moveToPreviousStep,
	moveToNextStep,
	moveToStep,
	canMoveToNextStep,
	canMoveToPreviousStep,
	selectedKycFile,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const { scrollFileCard } = useScrollFileCard({
		filesForStep,
		currentInvestor,
		selectedKycFile,
	});

	const handleStepNavigation = (direction: 'left' | 'right') => {
		if (direction === 'left') {
			const didFileScroll = scrollFileCard(direction);
			if (!didFileScroll) {
				moveToPreviousStep();
			}
		}
		if (direction === 'right') {
			const didFileScroll = scrollFileCard(direction);
			if (!didFileScroll) {
				moveToNextStep();
			}
		}
	};

	// shared actions between step click buttons and keyboard navigation
	const goToPreviousInvestorAction = () => {
		goToPreviousInvestor();
		moveToStep(0);
	};

	const goToNextInvestorAction = () => {
		goToNextInvestor();
		moveToStep(0);
	};

	const goToPreviousStepAction = () => {
		handleStepNavigation('left');
	};

	const goToNextStepAction = () => {
		handleStepNavigation('right');
	};

	// move between steps/investors
	useKeyboardNavigation([
		{ key: 'ArrowLeft', action: () => goToPreviousStepAction() },
		{ key: 'ArrowRight', action: () => goToNextStepAction() },
		{
			key: 'ArrowUp',
			action: () => goToPreviousInvestorAction(),
		},
		{
			key: 'ArrowDown',
			action: () => goToNextInvestorAction(),
		},
	]);

	const {
		goToNextInvestor,
		goToPreviousInvestor,
		isFirstInvestor,
		isLastInvestor,
		currentInvestorIndex,
		totalInvestors,
	} = useInvestorNavigation({
		investors: filteredInvestors || [],
	});

	const showCompleteKYCButton = isLastInvestor && !canMoveToNextStep;
	const showNextStepButton = !showCompleteKYCButton;
	const kycEntryPointUrl = searchParams.get('kycEntryPointUrl');

	const handleCompleteKYC = () => {
		// Navigate back to the stored entry point URL
		if (kycEntryPointUrl) {
			navigate(kycEntryPointUrl);
		}
	};

	return (
		<FlexExtended
			background="var(--Colour-greys-0, #F4F5F6)"
			borderTop="1px solid var(--Colour-greys-4, #D3D8DA)"
			borderRadius="0 0 0.25rem 0.25rem"
			justify="space-between"
			align="center"
			p="0.5rem 1.5rem"
		>
			<FlexExtended gap="0.5rem">
				<Button
					variant="default"
					leftSection={
						<Icon variant="caret-up" size="lg" style={{ marginRight: '0.25rem' }} />
					}
					onClick={() => goToPreviousInvestorAction()}
					disabled={isFirstInvestor}
					styles={(theme) => ({
						root: {
							backgroundColor: theme.colors.white[0],
							'&:hover': {
								backgroundColor: theme.colors.white[0],
							},
						},
					})}
				>
					{tMipManage('kyc.page.navigation.prev_investor')}
				</Button>
				<Button
					variant="default"
					leftSection={
						<Icon variant="caret-down" size="lg" style={{ marginRight: '0.25rem' }} />
					}
					onClick={() => goToNextInvestorAction()}
					disabled={isLastInvestor}
					styles={(theme) => ({
						root: {
							backgroundColor: theme.colors.white[0],
							'&:hover': {
								backgroundColor: theme.colors.white[0],
							},
						},
					})}
				>
					{tMipManage('kyc.page.navigation.next_investor')}
				</Button>
			</FlexExtended>
			<FlexExtended justify="center" align="center" style={{ flex: 1 }}>
				<Text fw={500} c="var(--Colour-greys-7, #6B7A7F)">
					{tMipManage('kyc.page.navigation.investor_counter', {
						currentInvestorIndex: currentInvestorIndex + 1,
						totalInvestors,
					})}
				</Text>
			</FlexExtended>
			<FlexExtended gap="0.5rem">
				<Button
					variant="default"
					leftSection={<Icon variant="chevron-left" size="lg" />}
					onClick={() => goToPreviousStepAction()}
					disabled={!canMoveToPreviousStep}
					styles={(theme) => ({
						root: {
							border: `1px solid ${theme.colors.blue[4]}`,
							'&:hover': {
								backgroundColor: theme.colors.white[0],
							},
						},
					})}
				>
					{tMipManage('kyc.page.navigation.prev_step')}
				</Button>

				{showNextStepButton && (
					<Button
						rightSection={<Icon variant="chevron-right" size="lg" />}
						onClick={() => goToNextStepAction()}
						disabled={!canMoveToNextStep}
						data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_KYC_REVIEW_NEXT_BUTTON}
					>
						{tMipManage('kyc.page.navigation.next_step')}
					</Button>
				)}

				{showCompleteKYCButton && (
					<Button
						rightSection={<Icon variant="chevron-right" size="lg" />}
						onClick={handleCompleteKYC}
						data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_KYC_REVIEW_COMPLETE_BUTTON}
					>
						{tMipManage('kyc.page.navigation.complete_kyc')}
					</Button>
				)}
			</FlexExtended>
		</FlexExtended>
	);
};
